import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { catchError, Observable, of, shareReplay, take } from 'rxjs';

import { gpSearchFields, hvSearchFields, pdSearchFields, schoolNurseSearchFields, sgSearchFields } from '@Constants';

import { FiltersService, AuthService } from '@Services';

import { User } from '@Models';

import { dateValidator, formatKey } from '@Utils';

import { MultiSelectionComponent } from '../multi-selection/multi-selection.component';
import { NoDataSelectComponent } from '../no-data-select/no-data-select.component';

@Component({
  selector: 'app-dashboard-form',
  standalone: true,
  imports: [ReactiveFormsModule, AsyncPipe, MultiSelectionComponent, NoDataSelectComponent],
  templateUrl: './dashboard-form.component.html',
  styleUrl: './dashboard-form.component.css',
})
export class DashboardFormComponent implements OnInit {
  searchForm!: FormGroup;
  formSubmitted = false;
  practices$: Observable<{ value: string; label: string }[]> | null = null;
  schools$: Observable<{ value: string; label: string }[]> | null = null;
  vaccines$: Observable<{ value: string; label: string }[]> | null = null;
  ethnicities$: Observable<{ value: string; label: string }[]> | null = null;
  wards$: Observable<{ value: string; label: string }[]> | null = null;
  imds$: Observable<{ value: string; label: string }[]> | null = null;
  schoolYears$: Observable<{ value: string; label: string }[]> | null = null;
  localEducationAuthorities$: Observable<{ value: string; label: string }[]> | null = null;
  localAuthorities$: Observable<{ value: string; label: string }[]> | null = null;
  refusalReasons$: Observable<{ value: string; label: string }[]> | null = null;
  flags$: Observable<{ value: string; label: string }[]> | null = null;

  dropdownSchoolArray: { key: string; value: string }[] = [
    { key: '0', value: '0' },
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' },
    { key: '4', value: '4' },
    { key: '5', value: '5' },
    { key: '6', value: '6' },
    { key: '7', value: '7' },
    { key: '8', value: '8' },
    { key: '9', value: '9' },
    { key: '10', value: '10' },
    { key: '11', value: '11' },
    { key: '12', value: '12' },
    { key: '13', value: '13' },
  ];

  dropdownAgeInYears: { key: string; value: string }[] = [
    { key: '0', value: '0' },
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' },
    { key: '4', value: '4' },
    { key: '5', value: '5' },
    { key: '6', value: '6' },
  ];

  upToDateOptions: { value: string; label: string }[] = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
    { label: 'No record', value: 'No record' },
  ];

  user: User | null = null;

  isDetailsOpen = true;
  appliedFilters: { key: string; label: string; values: string[] }[] = [];

  // Names for the Filters applied tags (FR-014), matching the labels on the form.
  private filterLabels: Record<string, string> = {
    startDateDoB: 'Date of Birth (From)',
    endDateDoB: 'Date of Birth (To)',
    postcode: 'Postcode',
    immunisationStatus: 'Immunisation Status',
    PracticeCode: 'GP Practice',
    SchoolYear: 'School year',
    AgeGroup: 'Age group',
    AgeInYears: 'Age',
    sex: 'Sex',
    ethnicity: 'Ethnicity',
    wardCode: 'Ward',
    imd: 'IMD Decile',
    UptoDate: 'Up to date',
    UpToDatePrimary: 'Up to date Primary',
    UpToDate12Month: 'Up to date Booster',
    UpToDatePreschool: 'Up to date Pre School',
    HasRefusals: 'Has Refusals',
    HasSuspensions: 'Has Suspensions',
    CurrentCover: 'Eligible for COVER in the quarter',
    NextCover: 'Eligible for Next COVER in the quarter',
    CohortIsNIPE72Hour: 'NIPE in 72h',
    CohortIsGP68WeeksReview: 'GP 6-8 Weeks Review',
    CohortIsNewbornBloodSpotScreening: 'Newborn BloodSpot Screening',
    CohortIsNewbornHearingScreening: 'Newborn Hearing Screening',
    sciD_Result: 'SCID Suspected',
    LocalEducationAuthorityCode: 'Local Education Authority',
    LocalAuthorityCode: 'Local Authority',
    SchoolCode: 'Schools',
    MissingVaccine: 'Missing Vaccines',
    RefusalReasons: 'Reason for Vaccine Not Given',
    Flags: 'Flags',
  };

  // Display names for fixed option values. Lookup fields (schools, practices…) get theirs from the API lists.
  private valueNames: Record<string, Record<string, string>> = {
    immunisationStatus: { NotFully: 'Not fully immunised', Fully: 'Fully immunised' },
    AgeGroup: { '0-5': '0 to 5', '6-19': '6 to 19' },
    sex: { f: 'F', m: 'M' },
    sciD_Result: { Suspected: 'Yes' },
  };

  // The Yes/No selects put true, false or yes in the URL.
  private yesNoNames: Record<string, string> = { true: 'Yes', false: 'No', yes: 'Yes' };

  private lookupNames: Record<string, Record<string, string>> = {};

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _fb: FormBuilder,
    private _filtersService: FiltersService,
    private _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();

    // Before the query params subscription, which needs these lists for the applied filter names.
    this.getAllPractices();
    this.getAllSchools();
    this.getAllVaccines();
    this.getAllEthnicities();
    this.getAllWards();
    this.getAllImds();
    this.getAllSchoolYears();
    this.getAllLocalAuthorities();
    this.getAllLocalAuthorityAreas();
    this.getAllRefusalReasons();
    this.getAllFlags();

    this._route.queryParams.subscribe((params) => {
      // The URL is what the results reflect, so rebuild the form from it. Starting from a clean form
      // means a filter removed from the URL is cleared from the form too.
      this.initializeForm();
      Object.keys(params).forEach((key) => {
        const control = this.searchForm.get(key);
        if (control) {
          // Multi-selects hold arrays, but a single value arrives from the URL as a plain string.
          control.setValue(Array.isArray(control.value) && !Array.isArray(params[key]) ? [params[key]] : params[key]);
        }
      });
      this.setAppliedFilters(params);
    });

    //get the user role
    const access_token = sessionStorage.getItem('access_token');
    const refresh_token = sessionStorage.getItem('refresh_token');
    if (access_token && refresh_token) {
      this._authService.user$.subscribe((user) => {
        if (!this.user && !user) {
          this._authService.setSession(access_token, refresh_token);
        }
      });
      this._authService.user$.subscribe((user) => {
        this.user = user;
      });
    }
  }

  initializeForm() {
    this.searchForm = this._fb.group(
      {
        startDateDoB: ['', dateValidator()],
        endDateDoB: ['', dateValidator()],
        postcode: [''],
        immunisationStatus: ['-1'],
        SchoolCode: [[]],
        SchoolYear: ['-2'],
        AgeGroup: ['-1'],
        AgeInYears: ['-1'],
        sex: ['-1'],
        ethnicity: ['-1'],
        wardCode: ['-1'],
        imd: ['-1'],
        englishFirstLanguage: ['-1'],
        UptoDate: [[]],
        UpToDatePrimary: [[]],
        UpToDate12Month: [[]],
        UpToDatePreschool: [[]],
        HasRefusals: ['-1'],
        HasSuspensions: ['-1'],
        PracticeCode: ['-1'],
        MissingVaccine: [[]],
        sciD_Result: ['-1'],
        CurrentCover: ['-1'],
        NextCover: ['-1'],
        CohortIsNIPE72Hour: ['-1'],
        CohortIsGP68WeeksReview: ['-1'],
        CohortIsNewbornBloodSpotScreening: ['-1'],
        CohortIsNewbornHearingScreening: ['-1'],
        LocalEducationAuthorityCode: [[]],
        LocalAuthorityCode: [[]],
        RefusalReasons: [[]],
        Flags: [[]],
        pageNumber: 1,
      },
      { updateOn: 'submit' },
    );
  }

  exceptionKeys = ['SchoolYear'];

  onSubmit() {
    this.formSubmitted = true;
    const formValues = this.searchForm.value;
    console.log(formValues);

    const queryParams = Object.keys(formValues)
      .filter((key) => {
        if (key === 'MissingVaccine' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (key === 'SchoolCode' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (key === 'UptoDate' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (this.exceptionKeys.includes(key)) {
          return formValues[key] !== null && formValues[key] !== '' && formValues[key] !== '-2';
        }
        return (
          formValues[key] !== null && formValues[key] !== '' && formValues[key] !== '-1' && formValues[key] !== '-2'
        );
      })
      .reduce((obj: Record<string, unknown>, key: string) => {
        // Expand MissingVaccine array into multiple params
        if (key === 'MissingVaccine' && Array.isArray(formValues[key])) {
          obj[key] = formValues[key].filter((val: string) => val !== '-1' && val !== '-2');
        } else {
          obj[key] = formValues[key];
        }
        return obj;
      }, {});

    queryParams['pageNumber'] = 1;

    // --- CUSTOM SERIALIZATION ---
    // Convert array params to repeated key-value pairs
    const expandedParams: Record<string, unknown> = {};
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (expandedParams[key]) {
            if (Array.isArray(expandedParams[key])) {
              expandedParams[key].push(v);
            } else {
              expandedParams[key] = [expandedParams[key], v];
            }
          } else {
            expandedParams[key] = [v];
          }
        });
      } else {
        expandedParams[key] = value;
      }
    });

    this._router
      .navigate([`/vaccine-history`], {
        queryParams: expandedParams,
      })
      .then(() => {
        (document.activeElement as HTMLElement).blur();
      });
  }

  toggleDetails(event: Event): void {
    this.isDetailsOpen = (event.target as HTMLDetailsElement).open;
  }

  // Built from the URL rather than the form, so the tags show what the results are actually based on.
  private setAppliedFilters(params: Params): void {
    this.appliedFilters = Object.keys(params)
      .filter((key) => key !== 'pageNumber')
      .map((key) => ({
        key,
        label: this.filterLabels[key] ?? formatKey(key),
        values: ([] as string[]).concat(params[key]).filter((value) => value !== ''),
      }))
      .filter((filter) => filter.values.length > 0);

    this.loadLookupNames(this.appliedFilters.map((filter) => filter.key));
  }

  private loadLookupNames(keys: string[]): void {
    const lists: Record<string, Observable<{ value: string; label: string }[]> | null> = {
      PracticeCode: this.practices$,
      SchoolCode: this.schools$,
      MissingVaccine: this.vaccines$,
      ethnicity: this.ethnicities$,
      wardCode: this.wards$,
      imd: this.imds$,
      LocalEducationAuthorityCode: this.localEducationAuthorities$,
      LocalAuthorityCode: this.localAuthorities$,
      RefusalReasons: this.refusalReasons$,
      Flags: this.flags$,
    };

    keys.forEach((key) => {
      const list$ = lists[key];
      if (!list$ || this.lookupNames[key]) {
        return;
      }
      list$
        .pipe(
          take(1),
          catchError(() => of([])),
        )
        .subscribe((list) => {
          this.lookupNames[key] = Object.fromEntries((list ?? []).map((item) => [String(item.value), item.label]));
        });
    });
  }

  filterValueLabel(key: string, value: string): string {
    return this.lookupNames[key]?.[value] ?? this.valueNames[key]?.[value] ?? this.yesNoNames[value] ?? value;
  }

  removeFilter(key: string, value: string): void {
    const current = this._route.snapshot.queryParamMap;
    const queryParams: Record<string, string | string[] | number> = {};
    current.keys.forEach((paramKey) => {
      const remaining = current.getAll(paramKey).filter((paramValue) => paramKey !== key || paramValue !== value);
      // A lone value must stay a plain string: the router hands it back as given, and the
      // single-choice fields (Has Refusals, Sex…) break on an array.
      if (remaining.length > 0) {
        queryParams[paramKey] = remaining.length === 1 ? remaining[0] : remaining;
      }
    });
    queryParams['pageNumber'] = 1;

    this._router.navigate(['/vaccine-history'], { queryParams }).then();
  }

  isNotEmpty(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    if (!control || !control.value) {
      return false;
    }

    // Multi-selects (Missing Vaccines, Flags…) hold arrays, which have no trim()
    if (Array.isArray(control.value)) {
      // Check if the array has any non-default values
      return control.value.length > 0 && control.value.some((value) => value !== '-1' && value !== '-2');
    }

    // Original logic for other controls
    if (this.exceptionKeys.includes(controlName)) {
      return control.value.trim() !== '' && control.value !== '-2';
    }
    return control.value.trim() !== '' && control.value !== '-1' && control.value !== '-2';
  }

  clearFilters() {
    this.initializeForm();
    (document.activeElement as HTMLElement).blur();

    this.onSubmit();
  }

  getAllPractices() {
    return (this.practices$ = this._filtersService.getAllPractices().pipe(shareReplay(1)));
  }

  getAllSchools() {
    return (this.schools$ = this._filtersService.getAllSchools().pipe(shareReplay(1)));
  }

  getAllVaccines() {
    return (this.vaccines$ = this._filtersService.getAllVaccines().pipe(shareReplay(1)));
  }

  getAllEthnicities() {
    return (this.ethnicities$ = this._filtersService.getAllEthnicities().pipe(shareReplay(1)));
  }

  getAllWards() {
    return (this.wards$ = this._filtersService.getAllWards().pipe(shareReplay(1)));
  }

  getAllImds() {
    return (this.imds$ = this._filtersService.getAllImds().pipe(shareReplay(1)));
  }

  getAllSchoolYears() {
    return (this.schoolYears$ = this._filtersService.getAllSchoolYears());
  }

  getAllLocalAuthorities() {
    return (this.localEducationAuthorities$ = this._filtersService.getAllLocalAuthorities().pipe(shareReplay(1)));
  }

  getAllLocalAuthorityAreas() {
    return (this.localAuthorities$ = this._filtersService.getAllLocalAuthorityAreas().pipe(shareReplay(1)));
  }

  getAllRefusalReasons() {
    return (this.refusalReasons$ = this._filtersService.getAllRefusalReasons().pipe(shareReplay(1)));
  }

  getAllFlags() {
    return (this.flags$ = this._filtersService.getAllFlags().pipe(shareReplay(1)));
  }

  shouldDisplayField(type?: string): boolean {
    switch (this.user?.role) {
      case 'gp':
        return gpSearchFields.includes(type ?? '');
      case 'pd':
        return pdSearchFields.includes(type ?? '');
      case 'hv':
        return hvSearchFields.includes(type ?? '');
      case 'sn':
        return schoolNurseSearchFields.includes(type ?? '');
      case 'sg':
        return sgSearchFields.includes(type ?? '');
      default:
        return false;
    }
  }

  getMissingVaccinesFromUrl(): string[] {
    const missingVaccine = this.searchForm.value['MissingVaccine'];

    // If missingVaccine is null or undefined, return an empty array
    if (missingVaccine == null) {
      return [];
    }

    // If missingVaccine is already an array, return it
    if (Array.isArray(missingVaccine)) {
      return missingVaccine;
    }

    // If missingVaccine is a string, convert it to an array with one element
    return [missingVaccine];
  }

  onMissingVaccineItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('MissingVaccine')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getUptoDateFromUrl(): string[] {
    return this.searchForm.value['UptoDate'];
  }

  onUptoDateItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('UptoDate')?.setValue(selectedValues);
    }
  }

  // Shared by the Up to date Primary, Booster and Pre School multi-selects (FR-011).
  // A single value arrives from the URL as a plain string, so wrap it.
  getSelectedFromUrl(controlName: string): string[] {
    const value = this.searchForm.value[controlName];
    if (value == null) {
      return [];
    }
    return Array.isArray(value) ? value : [value];
  }

  onItemsSelectedChange(controlName: string, selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get(controlName)?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getSchoolFromUrl(): string[] {
    return this.searchForm.value['SchoolCode'];
  }

  onSchoolItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('SchoolCode')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getLAFromUrl(): string[] {
    const la = this.searchForm.value['LocalEducationAuthorityCode'];

    // If missingVaccine is null or undefined, return an empty array
    if (la == null) {
      return [];
    }

    // If missingVaccine is already an array, return it
    if (Array.isArray(la)) {
      return la;
    }

    // If missingVaccine is a string, convert it to an array with one element
    return [la];
  }

  onLAItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('LocalEducationAuthorityCode')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getLocalAuthorityFromUrl(): string[] {
    const la = this.searchForm.value['LocalAuthorityCode'];
    if (la == null) {
      return [];
    }
    return Array.isArray(la) ? la : [la];
  }

  onLocalAuthorityItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('LocalAuthorityCode')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getRefusalReasonsFromUrl(): string[] {
    return this.searchForm.value['RefusalReasons'];
  }

  onRefusalReasonsItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('RefusalReasons')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }
}
